import useEvaluatedPermissions, {
    useEvaluatedPermissionsAcrossProfiles,
} from '../../../hooks/api/useEvaluatedPermissions';
import { APITypes } from '../../../primary-record-types';
import { PermissionDomains } from './types';

export const useIsProfileOwner = (medicalProfileId: string) =>
    useEvaluatedPermissions({ medicalProfileId }).permissions.isProfileOwner;

const getHasPermission = (
    appPermissions: APITypes.medicalProfileService.EvaluatedProfilePermissionsResponse,
    domain: PermissionDomains,
    permission: 'view' | 'edit',
) => {
    const permissionFromPolicy = appPermissions.permissions[domain];

    if (!permissionFromPolicy) return false;

    if (permission === 'view') {
        // view is allowed if policy allows view or edit
        return permissionFromPolicy === 'view' || permissionFromPolicy === 'edit';
    } else if (permission === 'edit') {
        // edit is only allowed if policy allows edit
        return permissionFromPolicy === 'edit';
    } else {
        return false;
    }
};

export const useHasPermission = (
    medicalProfileId: string | null,
    domain: PermissionDomains | null,
    permission: 'view' | 'edit',
) => {
    const evaluatedPermissions = useEvaluatedPermissions({ medicalProfileId });
    return medicalProfileId && domain ? getHasPermission(evaluatedPermissions.permissions, domain, permission) : false;
};

export const useHasPermissionAcrossProfiles = (
    medicalProfileIds: string[],
    domain: PermissionDomains,
    permission: 'view' | 'edit',
): string[] => {
    const evaluatedPermissions = useEvaluatedPermissionsAcrossProfiles({ medicalProfileIds });
    return medicalProfileIds.filter((medicalProfileId) =>
        getHasPermission(evaluatedPermissions.permissions[medicalProfileId], domain, permission),
    );
};

export const useIsLoadingPermissionsAcrossProfiles = (medicalProfileIds: string[]) =>
    useEvaluatedPermissionsAcrossProfiles({ medicalProfileIds }).isLoading;

export const usePermittedDomains = <TPermissionDomains extends Partial<PermissionDomains>>(
    medicalProfileId: string | null,
    domains: TPermissionDomains[],
    permission: 'view' | 'edit',
): TPermissionDomains[] => {
    const isLoadingPermissions = useIsLoadingPermissions(medicalProfileId);
    const evaluatedPermissions = useEvaluatedPermissions({ medicalProfileId });
    if (isLoadingPermissions) return [];

    const permittedDomains = domains
        .filter((domain) => getHasPermission(evaluatedPermissions.permissions, domain as PermissionDomains, permission))
        .map((domain) => domain as TPermissionDomains);
    return permittedDomains;
};

export const useGetHasPermission = (medicalProfileId: string | null) => {
    const evaluatedPermissions = useEvaluatedPermissions({ medicalProfileId });
    const isLoadingPermissions = useIsLoadingPermissions(medicalProfileId);

    return (domain: PermissionDomains, permission: 'view' | 'edit') => {
        if (!medicalProfileId || isLoadingPermissions) return false;

        return getHasPermission(evaluatedPermissions.permissions, domain, permission);
    };
};

export const useIsLoadingPermissions = (medicalProfileId: string | null) =>
    useEvaluatedPermissions({ medicalProfileId }).isLoading;
